import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../../../lib/supabase/database.types';
import {
  mapLeaderboardRow,
  type LeaderboardEntry,
  type LeaderboardRow,
} from '../models/leaderboard-entry';
import type { SubmitScorePayload } from '../models/submit-score-payload';
import type { SubmitScoreResult } from '../models/submit-score-result';

export interface LeaderboardRepository {
  getGlobalBest(): Promise<number | null>;
  getTopScores(limit?: number): Promise<LeaderboardEntry[]>;
  submitScore(payload: SubmitScorePayload): Promise<SubmitScoreResult>;
}

export class LeaderboardRequestError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export class SupabaseLeaderboardRepository implements LeaderboardRepository {
  private cache?: { expiresAt: number; entries: LeaderboardEntry[] };

  public constructor(
    private readonly client: SupabaseClient<Database> | null,
    private readonly now: () => number = Date.now,
  ) {}

  public async getTopScores(limit = 20): Promise<LeaderboardEntry[]> {
    const safeLimit = Math.min(20, Math.max(1, Math.floor(limit)));
    if (this.cache && this.cache.expiresAt > this.now()) {
      return this.cache.entries.slice(0, safeLimit);
    }
    if (!this.client) throw new LeaderboardRequestError('NETWORK_ERROR', 'Leaderboard is offline.');
    const { data, error } = await this.client
      .from('leaderboard')
      .select('id,nickname,score,created_at')
      .order('score', { ascending: false })
      .order('created_at', { ascending: true })
      .limit(20);
    if (error) throw new LeaderboardRequestError('SERVER_ERROR', 'Unable to load leaderboard.');
    const entries = (data as LeaderboardRow[]).map(mapLeaderboardRow);
    this.cache = { entries, expiresAt: this.now() + 45_000 };
    return entries.slice(0, safeLimit);
  }

  public async getGlobalBest(): Promise<number | null> {
    if (!this.client) throw new LeaderboardRequestError('NETWORK_ERROR', 'Leaderboard is offline.');
    const { data, error } = await this.client
      .from('leaderboard')
      .select('score')
      .order('score', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new LeaderboardRequestError('SERVER_ERROR', 'Unable to load global best.');
    if (data === null) return null;
    if (!Number.isInteger(data.score) || data.score < 0) {
      throw new LeaderboardRequestError('SERVER_ERROR', 'Global best returned invalid data.');
    }
    return data.score;
  }

  public async submitScore(payload: SubmitScorePayload): Promise<SubmitScoreResult> {
    if (!this.client) {
      throw new LeaderboardRequestError('NETWORK_ERROR', 'Score service is unavailable.');
    }
    const invocation: unknown = await this.client.functions.invoke('submit-score', {
      body: payload,
    });
    if (!isRecord(invocation)) {
      throw new LeaderboardRequestError('SERVER_ERROR', 'Score service returned invalid data.');
    }
    const data = parseSubmitResult(invocation.data);
    const error = invocation.error;
    if (error) {
      const apiError = await readFunctionError(error);
      throw new LeaderboardRequestError(
        apiError?.code ?? 'NETWORK_ERROR',
        apiError?.message ?? 'Unable to submit score.',
      );
    }
    if (!data) throw new LeaderboardRequestError('SERVER_ERROR', 'Score service returned no data.');
    this.cache = undefined;
    return data;
  }
}

const readFunctionError = async (
  error: unknown,
): Promise<{ code?: string; message?: string } | null> => {
  if (!isRecord(error) || !(error.context instanceof Response)) return null;
  try {
    const body: unknown = await error.context.clone().json();
    if (typeof body !== 'object' || body === null || Array.isArray(body)) return null;
    const record = body as Record<string, unknown>;
    return {
      code: typeof record.code === 'string' ? record.code : undefined,
      message: typeof record.message === 'string' ? record.message : undefined,
    };
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseSubmitResult = (value: unknown): SubmitScoreResult | null => {
  if (!isRecord(value) || typeof value.success !== 'boolean') return null;
  if (value.success) {
    if (
      typeof value.scoreId !== 'string' ||
      typeof value.score !== 'number' ||
      typeof value.rank !== 'number'
    ) {
      return null;
    }
    return {
      success: true,
      scoreId: value.scoreId,
      score: value.score,
      rank: value.rank,
      idempotent: typeof value.idempotent === 'boolean' ? value.idempotent : undefined,
    };
  }
  if (typeof value.code !== 'string' || typeof value.message !== 'string') return null;
  return {
    success: false,
    code: value.code,
    message: value.message,
    field: typeof value.field === 'string' ? value.field : undefined,
    requestId: typeof value.requestId === 'string' ? value.requestId : undefined,
  };
};
